import { Base } from '@itsrever/design-system'
import { GetInvoiceFileURLFileEnum, InvoicingapipbInvoice } from '@itsrever/rever-api'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { formatAmount, formatDate, formatPeriod } from './invoice-row'
import { invoiceState, stateTone } from './invoice-status'

const { Badge, Button, Card, EmptyState, Table, createColumnHelper } = Base

const column = createColumnHelper<InvoicingapipbInvoice>()

const RIGHT = {
  meta: { cellClassName: 'text-right', headClassName: 'text-right' }
}

interface InvoicesTableProps {
  invoices: InvoicingapipbInvoice[]
  loading: boolean
  error: boolean
  pendingFile?: { invoiceId: string; file: GetInvoiceFileURLFileEnum }
  onRetry: () => void
  onDownload: (invoice: InvoicingapipbInvoice, file: GetInvoiceFileURLFileEnum) => void
}

export const InvoicesTable: React.FC<InvoicesTableProps> = ({
  invoices,
  loading,
  error,
  pendingFile,
  onRetry,
  onDownload
}) => {
  const { t, i18n } = useTranslation()

  const documents = (invoice: InvoicingapipbInvoice) => (
    <div className="flex justify-end gap-1">
      {invoice.invoice_url ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => window.open(invoice.invoice_url, '_blank')}
        >
          {t('billing.invoice')}
        </Button>
      ) : null}
      {invoice.has_breakdown ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          loading={isPending(pendingFile, invoice, 'INVOICE_FILE_BREAKDOWN')}
          onClick={() => onDownload(invoice, 'INVOICE_FILE_BREAKDOWN')}
        >
          {t('billing.breakdown')}
        </Button>
      ) : null}
      {invoice.has_supplied_note ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          loading={isPending(
            pendingFile,
            invoice,
            'INVOICE_FILE_SUPPLIED_NOTE'
          )}
          onClick={() => onDownload(invoice, 'INVOICE_FILE_SUPPLIED_NOTE')}
        >
          {t('billing.supplied_note')}
        </Button>
      ) : null}
    </div>
  )

  const columns = [
    column.display({
      id: 'period',
      header: t('billing.column_period'),
      cell: ({ row }) => (
        <span className="font-medium">
          {formatPeriod(row.original.period ?? '', i18n.language)}
        </span>
      )
    }),
    column.display({
      id: 'issued',
      header: t('billing.column_issued'),
      cell: ({ row }) =>
        row.original.issued_at
          ? formatDate(new Date(row.original.issued_at), i18n.language)
          : '',
      meta: { cellClassName: 'text-neutral-600' }
    }),
    column.display({
      id: 'amount',
      header: t('billing.column_amount'),
      cell: ({ row }) =>
        formatAmount(
          Number(row.original.total ?? 0),
          row.original.currency ?? '',
          i18n.language
        ),
      ...RIGHT
    }),
    column.display({
      id: 'status',
      header: t('billing.column_status'),
      cell: ({ row }) => status(row.original, t)
    }),
    column.display({
      id: 'documents',
      header: t('billing.column_documents'),
      cell: ({ row }) => documents(row.original),
      ...RIGHT
    })
  ]

  // An error must not read as an empty history: "no invoices" on a billing page is a reason to
  // open a ticket.
  if (error) {
    return (
      <EmptyState
        variant="card"
        title={t('billing.load_error')}
        actions={
          <Button type="button" variant="secondary" onClick={onRetry}>
            {t('billing.retry')}
          </Button>
        }
      />
    )
  }

  if (!loading && invoices.length === 0) {
    return (
      <EmptyState
        variant="card"
        title={t('billing.empty')}
        description={t('billing.empty_description')}
      />
    )
  }

  return (
    <Card className="h-auto overflow-hidden">
      <Table
        data={invoices}
        columns={columns}
        loading={loading}
        getRowId={(invoice) => invoice.id ?? ''}
      />
    </Card>
  )
}

function status(invoice: InvoicingapipbInvoice, t: TFunction) {
  const state = invoiceState(invoice)
  if (!state) return invoice.status ?? ''
  return (
    <Badge tone={stateTone(state)} variant="soft">
      {t(`billing.status_${state}`)}
    </Badge>
  )
}

function isPending(
  pendingFile: InvoicesTableProps['pendingFile'],
  invoice: InvoicingapipbInvoice,
  file: GetInvoiceFileURLFileEnum
): boolean {
  return pendingFile?.invoiceId === invoice.id && pendingFile?.file === file
}
